import { useState } from 'react'
import {
  Check,
  Zap,
  TrendingUp,
  Users,
  DollarSign,
  Target,
  Shield,
  Globe,
  BarChart3,
  Music,
  MessageSquare,
  FolderKanban,
  RefreshCw,
  Mic2,
  Plane,
  Brain,
  Award,
  Star,
  MapPin,
  Handshake,
  Wifi,
} from 'lucide-react'
import BrandLogo from '../components/BrandLogo'

/* ═══════════════════════════════════════════════════════════════════════════
   DIZEE — Investor Pitch Deck
   Password-protected, fullscreen slide presentation.
   ═══════════════════════════════════════════════════════════════════════════ */

const DECK_PASSWORD = 'dizee2025'

export default function DeckPage() {
  const [authenticated, setAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [error, setError] = useState(false)

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    if (password === DECK_PASSWORD) {
      setAuthenticated(true)
      setError(false)
    } else {
      setError(true)
    }
  }

  if (!authenticated) {
    return (
      <div className="fixed inset-0 flex items-center justify-center p-8" style={{ backgroundColor: '#13120A' }}>
        <div className="w-full max-w-md">
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-5 h-5 bg-[#FF2300] rounded-sm flex-shrink-0" />
              <BrandLogo className="h-3 w-auto text-white" />
            </div>
            <p className="text-sm text-zinc-500">Enter the password to view this deck.</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm text-zinc-300 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  setError(false)
                }}
                placeholder="Password"
                autoFocus
                className="w-full rounded-lg border border-white/[0.08] bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-[#FF2300]/50 focus:border-transparent"
              />
              {error && (
                <p className="mt-2 text-xs text-red-400">Incorrect password. Try again.</p>
              )}
            </div>
            <button
              type="submit"
              className="w-full rounded-lg bg-white px-4 py-3 text-sm font-semibold text-zinc-950 hover:bg-zinc-100 transition-colors"
            >
              View Deck
            </button>
          </form>
          <p className="mt-8 text-[11px] text-zinc-700">
            Confidential — For intended recipients only.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: '#13120A' }}>
      <div className="fixed top-6 right-8 z-50">
        <span className="text-[10px] font-medium text-zinc-800 uppercase tracking-widest">
          Confidential
        </span>
      </div>

      <div className="max-w-[1400px] mx-auto px-8 sm:px-16 lg:px-24">
        {SLIDES.map(({ component: Section }, i) => (
          <section
            key={i}
            className={`py-24 sm:py-32 ${i === 0 ? 'min-h-screen flex flex-col justify-center' : ''} ${i < SLIDES.length - 1 ? 'border-b border-white/[0.04]' : ''}`}
          >
            <Section />
          </section>
        ))}
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   SLIDE DEFINITIONS
   ═══════════════════════════════════════════════════════════════════════════ */

interface Slide {
  title: string
  component: React.FC
}

/* ── Slide 1: Title ────────────────────────────────────────────────────── */
function SlideTitle() {
  return (
    <div className="flex flex-col justify-center">
      <div className="flex items-center gap-2.5 mb-8">
        <div className="w-6 h-6 bg-[#FF2300] rounded-sm flex-shrink-0" />
        <BrandLogo className="h-4 w-auto text-white" />
      </div>
      <div className="mt-12 flex items-center gap-3">
        <span className="inline-flex items-center rounded-full border border-[#FF2300]/30 bg-[#FF2300]/10 px-4 py-1.5 text-[12px] font-medium text-[#FF2300]">
          Pre-Seed
        </span>
        <span className="text-[13px] text-zinc-500">February 2026</span>
      </div>
      <div className="mt-8">
        <p className="text-sm text-zinc-500">www.dizee.com</p>
      </div>
    </div>
  )
}

/* ── Slide 2: Mission / Tagline ────────────────────────────────────────── */
function SlideMission() {
  return (
    <div className="flex flex-col justify-center min-h-[50vh]">
      <h2 className="text-3xl sm:text-4xl lg:text-[56px] font-semibold tracking-tighter text-white leading-[1.08] max-w-4xl">
        The new operating system and{' '}
        <span className="text-[#FF2300]">source of truth</span> for modern music careers.
      </h2>
      <div className="mt-8 max-w-2xl">
        <p className="text-base sm:text-lg text-zinc-400 leading-relaxed">
          So artists, managers, and their teams stay aligned around a single source of truth.
        </p>
      </div>
    </div>
  )
}

/* ── Slide 3: What's Happening Now ─────────────────────────────────────── */
function SlideWhatsHappening() {
  return (
    <div>
      <SlideTag>What&apos;s Happening Now</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        The industry has shifted.{' '}
        <span className="text-[#FF2300]">Fundamentally.</span>
      </h2>

      <div className="mt-12 grid sm:grid-cols-2 gap-5">
        <TrendCard title="Industry Shift" accent="orange">
          The music industry is undergoing a seismic technological and cultural shift.
        </TrendCard>
        <TrendCard title="Decentralized Power" accent="orange">
          Power has decentralized. Labels and traditional gatekeepers no longer manage most careers.
        </TrendCard>
        <TrendCard title="Independent Era" accent="orange">
          Independent artists, managers, and small teams now run their own businesses.
        </TrendCard>
        <TrendCard title="Infrastructure Gap" accent="orange">
          Responsibility has shifted, but the operating infrastructure never followed.
        </TrendCard>
      </div>
    </div>
  )
}

/* ── Slide 4: Industry Trends ──────────────────────────────────────────── */
function SlideIndustryTrends() {
  return (
    <div>
      <SlideTag>Industry Trends</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        What&apos;s coming next.
      </h2>

      {/* Big stats */}
      <div className="mt-12 grid sm:grid-cols-2 gap-4">
        <div className="rounded-xl border border-[#FF2300]/20 bg-[#FF2300]/[0.04] p-8">
          <p className="text-5xl sm:text-6xl font-bold tracking-tight text-[#FF2300]">75.9M</p>
          <p className="text-sm text-zinc-400 mt-2">Music Creators (2023)</p>
          <p className="text-[12px] text-zinc-600 mt-1">Global estimate</p>
          <a href="https://routenote.com/blog/nearly-200-million-music-creators-predicted-by-2030/" target="_blank" rel="noopener noreferrer" className="block text-[10px] text-zinc-700 mt-3 hover:text-zinc-500 transition-colors">Source: RouteNote (citing MIDiA Research)</a>
        </div>
        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] p-8">
          <p className="text-5xl sm:text-6xl font-bold tracking-tight text-emerald-400">198.2M</p>
          <p className="text-sm text-zinc-400 mt-2">Projected by 2030</p>
          <p className="text-[12px] text-zinc-600 mt-1">Music creators globally</p>
          <a href="https://routenote.com/blog/nearly-200-million-music-creators-predicted-by-2030/" target="_blank" rel="noopener noreferrer" className="block text-[10px] text-zinc-700 mt-3 hover:text-zinc-500 transition-colors">Source: RouteNote (citing MIDiA Research)</a>
        </div>
      </div>

      {/* Trend items */}
      <div className="mt-10 grid sm:grid-cols-2 gap-4">
        <TrendCard title="New AI Tools" accent="emerald">
          The market for Gen AI music &amp; audiovisual content could rise from €3B to €64B by 2028.
          <a href="https://www.cisac.org/Newsroom/news-releases/global-economic-study-shows-human-creators-future-risk-generative-ai" target="_blank" rel="noopener noreferrer" className="block text-[11px] text-zinc-600 mt-2 hover:text-zinc-400 transition-colors">Source: CISAC Global Economic Study (2024)</a>
        </TrendCard>
        <TrendCard title="Barrier to Entry" accent="emerald">
          120,000 new tracks hit streaming services each day (Q1 2023 average).
          <a href="https://www.musicbusinessworldwide.com/there-are-now-120000-new-tracks-hitting-music-streaming-services-each-day/" target="_blank" rel="noopener noreferrer" className="block text-[11px] text-zinc-600 mt-2 hover:text-zinc-400 transition-colors">Source: Music Business Worldwide (Luminate data)</a>
        </TrendCard>
        <TrendCard title="Rise of the Creators" accent="emerald">
          Creator Economy market size: $252.33B (2025).
          <a href="https://www.grandviewresearch.com/industry-analysis/creator-economy-market-report" target="_blank" rel="noopener noreferrer" className="block text-[11px] text-zinc-600 mt-2 hover:text-zinc-400 transition-colors">Source: Grand View Research</a>
        </TrendCard>
        <TrendCard title="Fragmented Ecosystem" accent="emerald">
          Performance rights revenue reached $2.9B in 2024 (+5.9%), adding another major revenue stream creators must manage.
          <a href="https://www.ifpi.org/ifpi-amidst-highly-competitive-market-global-recorded-music-revenues-grew-4-8-in-2024/" target="_blank" rel="noopener noreferrer" className="block text-[11px] text-zinc-600 mt-2 hover:text-zinc-400 transition-colors">Source: IFPI Global Music Report 2025 (Press Release)</a>
        </TrendCard>
      </div>

      <div className="mt-10 pt-6 border-t border-white/[0.06]">
        <p className="text-xl sm:text-2xl font-medium tracking-tight text-white">
          The industry has changed.{' '}
          <span className="text-zinc-500">The tools haven&apos;t.</span>
        </p>
      </div>
    </div>
  )
}

/* ── Slide 5: Problem (Current Tools) ────────────────────────────────────── */
function SlideProblemCurrentTools() {
  return (
    <div>
      <SlideTag>The Problem — Current State</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        Today, music businesses still run on:
      </h2>

      <div className="mt-12 grid sm:grid-cols-2 gap-5">
        <BrokenToolCard icon={<Zap className="w-5 h-5" />} title="Manual Processes">
          Manual processes and over-complicated workflows. Nothing is automated or connected.
        </BrokenToolCard>
        <BrokenToolCard icon={<MessageSquare className="w-5 h-5" />} title="Fragmented Communication">
          Spreadsheets, text messages, and email threads. Critical decisions live in iMessage conversations that nobody can search or reference.
        </BrokenToolCard>
        <BrokenToolCard icon={<Shield className="w-5 h-5" />} title="Legacy Tools">
          Legacy tools built for a different era. The music industry moves fast and decisions change constantly — legacy tools can&apos;t keep up.
        </BrokenToolCard>
        <BrokenToolCard icon={<Globe className="w-5 h-5" />} title="Disconnected Systems">
          Disconnected systems with no single source of truth. Data lives in a dozen different platforms with no way to connect the dots.
        </BrokenToolCard>
      </div>

      <div className="mt-14 rounded-xl border border-[#FF2300]/30 bg-[#FF2300]/[0.06] p-8">
        <p className="text-xl sm:text-2xl font-semibold text-white">
          There is no unified system to manage the modern music business.
        </p>
        <p className="text-sm text-zinc-400 mt-3">
          This leads to missed opportunities and lost revenue.
        </p>
      </div>
    </div>
  )
}

/* ── Slide 6: Problem (High Level) ──────────────────────────────────────── */
function SlideProblemHighLevel() {
  return (
    <div>
      <SlideTag>The Problem</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        Artists lack clear, real-time visibility{' '}
        <span className="text-[#FF2300]">into what&apos;s happening across their career.</span>
      </h2>

      <div className="mt-12 max-w-3xl space-y-8">
        <ProblemStatement icon={<Shield className="w-5 h-5" />}>
          Managers don&apos;t have a centralized system, the time, or reliable access to data needed to make informed decisions.
        </ProblemStatement>

        <ProblemStatement icon={<Zap className="w-5 h-5" />}>
          Today&apos;s tools are slow, fragmented, and static — making it easy to miss moments that matter. By the time data is gathered, the opportunity has passed.
        </ProblemStatement>

        <ProblemStatement icon={<MessageSquare className="w-5 h-5" />}>
          Communication between teams is chaotic — iMessage, email, text threads, and DMs with no single source of truth. Nothing is tracked, searchable, or connected.
        </ProblemStatement>
      </div>
    </div>
  )
}

/* ── Slide 7: Example (Artist Messages) ──────────────────────────────────── */
function SlideExampleMessages() {
  return (
    <div>
      <SlideTag>Real Conversations</SlideTag>
      <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tighter text-white">
        This is what managing a music career looks like today.
      </h2>

      <div className="mt-12 grid sm:grid-cols-3 gap-4">
        {[
          { from: 'Manager', msg: 'Did we ever get the splits sorted for the remix?', time: '2:14 PM' },
          { from: 'Artist', msg: 'Wait which remix? The one from last month or the new one?', time: '2:18 PM' },
          { from: 'Manager', msg: 'The one with the feature. I think the splits were in a google doc somewhere...', time: '2:22 PM' },
          { from: 'Booking Agent', msg: 'Need to confirm the Denver date by EOD. Is the routing finalized?', time: '3:05 PM' },
          { from: 'Manager', msg: 'Let me check the spreadsheet. Actually which spreadsheet has the routing?', time: '3:12 PM' },
          { from: 'Tour Manager', msg: 'I sent the updated routing last week via email. Did you not get it?', time: '3:30 PM' },
          { from: 'Artist', msg: 'When do we get paid for the festival? I thought that was supposed to clear already', time: '4:01 PM' },
          { from: 'Manager', msg: 'Let me dig through emails and find the contract...', time: '4:15 PM' },
          { from: 'Label A&R', msg: 'Can you send over the final master + metadata for the single?', time: '5:00 PM' },
        ].map((item, i) => (
          <div key={i} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-semibold text-zinc-500">{item.from}</span>
              <span className="text-[10px] text-zinc-700">{item.time}</span>
            </div>
            <p className="text-[12px] text-zinc-400 leading-relaxed italic">&ldquo;{item.msg}&rdquo;</p>
          </div>
        ))}
      </div>

      <div className="mt-8 max-w-xl">
        <p className="text-[13px] text-zinc-500">
          Scattered across iMessage, email, Slack, DMs, and spreadsheets. Nothing is tracked. Nothing is connected. This is the norm.
        </p>
      </div>
    </div>
  )
}

/* ── Slide 8: DIZEE Centralizes ────────────────────────────────────────── */
function SlideCentralizes() {
  return (
    <div>
      <SlideTag>The Solution</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        DIZEE centralizes the modern music career{' '}
        <span className="text-[#FF2300]">into a single operating system and source of truth.</span>
      </h2>

      <div className="mt-10 max-w-3xl space-y-6">
        <p className="text-lg sm:text-xl text-zinc-300 leading-relaxed">
          Anyone can now manage a modern music career end-to-end with DIZEE.
        </p>
        <p className="text-lg sm:text-xl text-zinc-400 leading-relaxed">
          It gives artists and managers real-time clarity so they can move faster, make better decisions, and capture revenue as it happens.
        </p>
      </div>

      <div className="mt-12 grid sm:grid-cols-3 gap-6">
        <SolutionCard
          icon={<BarChart3 className="w-5 h-5 text-[#FF2300]" />}
          title="Real-Time Clarity"
          description="A unified view of your entire music career — releases, shows, finances, team — updated in real time, in one place."
        />
        <SolutionCard
          icon={<Brain className="w-5 h-5 text-[#FF2300]" />}
          title="Smarter Decisions"
          description="AI-powered insights and recommendations surface opportunities as they happen so you can act, not react."
        />
        <SolutionCard
          icon={<Target className="w-5 h-5 text-[#FF2300]" />}
          title="End-to-End Management"
          description="From catalog and release management to touring, team communication, and project management — all connected."
        />
      </div>
    </div>
  )
}

/* ── Slide 10: Product — Platform & Modules ────────────────────────────── */
function SlideProduct() {
  return (
    <div>
      <SlideTag>Product</SlideTag>
      <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tighter text-white">
        A unified platform that centralizes your career.
      </h2>

      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <ProductFeature icon={<Brain className="w-4 h-4" />} title="AI Career Assistant" description="Answers questions, surfaces insights and recommendations, and automates tasks" />
        <ProductFeature icon={<BarChart3 className="w-4 h-4" />} title="Dashboard" description="A real-time view of all pieces of your music career in one place" />
        <ProductFeature icon={<Users className="w-4 h-4" />} title="Teams" description="Connect your team members and associates to streamline communication" />
        <ProductFeature icon={<Music className="w-4 h-4" />} title="Catalog & Releases" description="Complete discography, metadata, splits, credits" />
        <ProductFeature icon={<Mic2 className="w-4 h-4" />} title="Bookings" description="Tour dates, promoter CRM, revenue insights, calendars" />
        <ProductFeature icon={<Plane className="w-4 h-4" />} title="Tour Manager" description="Organize and streamline touring operations, travel, and communication" />
        <ProductFeature icon={<FolderKanban className="w-4 h-4" />} title="Project Management" description="Tasks, notes, files, team collaboration" />
        <ProductFeature icon={<RefreshCw className="w-4 h-4" />} title="24/7 Sync" description="Google Sheets, email forwarding, platform integrations, DIZEE Email system" />
      </div>
    </div>
  )
}

/* ── Slide 11: Market Size (Segments) ──────────────────────────────────── */
function SlideMarketSize() {
  return (
    <div>
      <SlideTag>Market Size</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        A massive, underserved market{' '}
        <span className="text-[#FF2300]">growing every year.</span>
      </h2>

      <div className="mt-10 grid lg:grid-cols-3 gap-6">
        <MarketSegmentCard
          label="TAM — Creator Infrastructure Software"
          description="3% of $252B Global Creator Economy (2025)"
          stat="$7.5B"
          statLabel="3% of $252B Global Creator Economy (2025)"
          note="Vertical infrastructure platforms typically capture 2–5% of total ecosystem value. 3% of $252B represents the addressable creator software layer."
          color="red"
          source="Grand View Research (2025)"
          sourceUrl="https://www.grandviewresearch.com/industry-analysis/creator-economy-market-report"
        />
        <MarketSegmentCard
          label="SAM — Music (Initial Wedge)"
          description="~13M artists tracked globally (2025)"
          stat="$750M"
          statLabel="~13M artists tracked globally (2025)"
          note="Music represents ~10% of creator infrastructure spend. This is Dizee's focused entry segment."
          color="violet"
          source="Chartmetric Year in Music 2025"
          sourceUrl="https://routenote.com/blog/chartmetric-music-in-report-year-2025/"
        />
        <MarketSegmentCard
          label="SOM — Professional Artist Teams"
          description="Managed / team-operated artists"
          stat="$15–20M"
          statLabel="ARR"
          note="Targeting professional, actively releasing artists with structured teams. Initial capture within music before expanding horizontally."
          color="emerald"
        />
      </div>
    </div>
  )
}

/* ── Slide 12: Business Model ─────────────────────────────────────────── */
function SlideBusinessModel() {
  return (
    <div>
      <SlideTag>Business Model</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        Simple subscription.{' '}
        <span className="text-[#FF2300]">Predictable revenue.</span>
      </h2>

      <div className="mt-10 grid lg:grid-cols-3 gap-4">
        <PricingCard
          tier="Artist"
          subtitle="Starter Plan"
          price="$45"
          note="/mo"
          audience="Single user"
          features={[
            'Dashboard',
            'Catalog & Release Management',
            'Career Assistant',
            'Schedule & Calendar',
            'Tasks',
            '24/7 Sync',
          ]}
        />
        <PricingCard
          tier="Artist Pro"
          price="$75"
          note="/mo"
          audience="Single user"
          highlighted
          features={[
            'Dashboard',
            'Catalog & Release Management',
            'Career Assistant',
            'Schedule & Calendar',
            'Tasks',
            '24/7 Sync',
          ]}
          plusFeatures={[
            'Ticket Sales Tracker',
            'Tour Manager',
            'Dizee AI Assistant',
            'Finance Tracking',
          ]}
        />
        <PricingCard
          tier="Enterprise"
          price="$125"
          note="/mo"
          audience="2+ users ($15 per additional seat)"
          features={[
            'Dashboard',
            'Catalog & Release Management',
            'Career Assistant',
            'Schedule & Calendar',
            'Tasks',
            '24/7 Sync',
            'Tour Manager',
            'Finance Tracking',
            'Ticket Sales Tracker',
            'Dizee AI Assistant',
          ]}
          plusFeatures={[
            'Teams',
            'Songwriter Credits',
            'Dedicated Customer Support',
          ]}
        />
      </div>

      <div className="mt-8">
        <p className="text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-3">Additional Revenue Streams</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          <RevenueStream>AI usage credits</RevenueStream>
          <RevenueStream>Advanced ingestion add-ons</RevenueStream>
          <RevenueStream>Integrations marketplace</RevenueStream>
          <RevenueStream>Financial tools (future)</RevenueStream>
          <RevenueStream>Sponsorship intelligence (future)</RevenueStream>
        </div>
      </div>
    </div>
  )
}

/* ── Slide 13: Go-To-Market ──────────────────────────────────────────────── */
function SlideGTM() {
  return (
    <div>
      <SlideTag>Go-To-Market</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        Three phases.{' '}
        <span className="text-[#FF2300]">Founder-led to scalable.</span>
      </h2>

      <div className="mt-12 grid lg:grid-cols-3 gap-6">
        <GTMPhaseCard
          phase="Phase 1"
          title="Founder-Led Wedge"
          timeline="Months 0–6"
          target="First 200 accounts"
          items={[
            'Direct outreach to independent managers (warm intros + cold)',
            '1:1 onboarding calls',
            'Early product feedback loop',
          ]}
        />
        <GTMPhaseCard
          phase="Phase 2"
          title="Signal Amplification"
          timeline="Months 6–12"
          target="500–700 accounts"
          items={[
            'Case studies from credible managers',
            'Referral incentives',
            'Partnerships with boutique agencies',
            'Public testimonials + social proof',
          ]}
        />
        <GTMPhaseCard
          phase="Phase 3"
          title="Scalable Acquisition"
          timeline="Months 12–24"
          target="1,500+ accounts"
          items={[
            'Content + education (manager-focused)',
            'Partnerships embedded into workflow tools',
            'Referral engine',
            'API ecosystem integrations',
          ]}
        />
      </div>

      <div className="mt-10 pt-6 border-t border-white/[0.06]">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div>
            <p className="text-[11px] font-semibold text-zinc-600 uppercase tracking-widest mb-1">Initial ICP</p>
            <p className="text-lg font-bold text-white">10,000</p>
            <p className="text-[11px] text-zinc-500">serviceable managers</p>
          </div>
          <div>
            <p className="text-[11px] font-semibold text-zinc-600 uppercase tracking-widest mb-1">24-Month Goal</p>
            <p className="text-lg font-bold text-[#FF2300]">15%</p>
            <p className="text-[11px] text-zinc-500">penetration over 24 months</p>
          </div>
          <div className="lg:col-span-2">
            <p className="text-[11px] font-semibold text-zinc-600 uppercase tracking-widest mb-2">Channel Mix</p>
            <div className="flex gap-4">
              <ChannelBar label="Direct Outreach" pct={40} />
              <ChannelBar label="Referrals" pct={35} />
              <ChannelBar label="Partnerships" pct={25} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ── Slide 14: Competition ──────────────────────────────────────────────── */
function SlideCompetition() {
  return (
    <div>
      <SlideTag>Competition</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        From static software{' '}
        <span className="text-[#FF2300]">to an intelligent operating system.</span>
      </h2>

      <div className="mt-10">
        <CompetitionQuadrant />
      </div>
    </div>
  )
}

/* ── Slide 15: Competitive Advantages ────────────────────────────────────── */
function SlideAdvantages() {
  return (
    <div>
      <SlideTag>Competitive Advantages</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        Why DIZEE wins.
      </h2>

      <div className="mt-12 grid sm:grid-cols-2 gap-4">
        <AdvantageCard icon={<Award className="w-5 h-5 text-[#FF2300]" />}>
          <strong className="text-white">Built by a 2x Grammy-nominated manager</strong> responsible for over 2 billion streams — deep, first-hand understanding of the full artist career lifecycle.
        </AdvantageCard>
        <AdvantageCard icon={<Music className="w-5 h-5 text-emerald-400" />}>
          <strong className="text-white">CEO with proven industry credibility</strong> and an active Billboard-charting record label. Rare combination of operator and domain expertise.
        </AdvantageCard>
        <AdvantageCard icon={<Users className="w-5 h-5 text-violet-400" />}>
          <strong className="text-white">Rare hybrid team</strong> of music insiders and modern product builders — experienced founders with growth-venture operating and capital-raising experience.
        </AdvantageCard>
        <AdvantageCard icon={<Brain className="w-5 h-5 text-amber-400" />}>
          <strong className="text-white">DIZEE AI knowledge base</strong> informed by 15+ years of lived industry experience and real career data — not generic, but music-native intelligence.
        </AdvantageCard>
        <AdvantageCard icon={<Globe className="w-5 h-5 text-[#FF2300]" />}>
          <strong className="text-white">Strong global relationships</strong> across artists, agencies, promoters, and labels — a built-in distribution network from day one.
        </AdvantageCard>
        <AdvantageCard icon={<DollarSign className="w-5 h-5 text-emerald-400" />}>
          <strong className="text-white">Capital-efficient</strong> — agile, lean operating team. Built to move fast, ship product, and scale without excess overhead.
        </AdvantageCard>
        <AdvantageCard icon={<MapPin className="w-5 h-5 text-violet-400" />}>
          <strong className="text-white">Based in Los Angeles</strong> — the center of global music culture, with direct access to the artists, managers, and labels shaping the industry.
        </AdvantageCard>
        <AdvantageCard icon={<TrendingUp className="w-5 h-5 text-amber-400" />}>
          <strong className="text-white">First-mover in a new category.</strong> No one has built a true operating system for the modern music career. DIZEE is defining the space.
        </AdvantageCard>
      </div>
    </div>
  )
}

/* ── Slide 16: Team ──────────────────────────────────────────────────────── */
function SlideTeam() {
  return (
    <div>
      <SlideTag>Team</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tighter text-white">
        The team building this.
      </h2>

      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <TeamCard
          name="Ryan Thomas"
          role="CEO"
          points={[
            '15+ years in the dance music industry',
            'Managed Grammy-nominated and platinum-selling artists',
            'Deep global relationships with agencies, promoters, and labels',
          ]}
        />
        <TeamCard
          name="Edward Contreras"
          role="CTO"
          points={[
            'Technologist, product builder, and entrepreneur',
            'Expertise in AI systems, music technology, and multi-product ecosystems',
            "Architect behind DIZEE's platform and integration layer",
          ]}
        />
        <TeamCard
          name="Aaron P. Wong"
          role="Board member and Strategic Advisor"
          points={[
            'Founder of Super73, a globally recognized electric bike company',
            'Experienced in scaling hardware businesses and social presence',
            'Raised and closed multiple rounds of venture funding',
          ]}
        />
        <TeamCard
          name="Owais Ghaus"
          role="CMO"
          points={[
            'Artist, marketer, and technologist',
            'Music executive with deep industry connections',
            'Specializes in content strategy, creator marketing, and artist development',
          ]}
        />
        <TeamCard
          name="Christopher Alba"
          role="CBO"
          points={[
            'Business development and strategic partnerships',
            'Deep network across music and entertainment verticals',
            'Focused on enterprise and label partnerships',
          ]}
        />
      </div>
    </div>
  )
}

/* ── Slide 17: The Ask ──────────────────────────────────────────────────── */
function SlideAsk() {
  return (
    <div>
      <SlideTag>The Ask</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        Raising <span className="text-[#FF2300]">$2.5M</span> to accelerate.
      </h2>

      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <UseOfFundsCard
          icon={<Target className="w-5 h-5 text-[#FF2300]" />}
          title="Product–Market Fit"
          description="Validate with real artists and managers — iterating on the product with active users in the music industry"
        />
        <UseOfFundsCard
          icon={<Shield className="w-5 h-5 text-[#FF2300]" />}
          title="Core Platform Development"
          description="System hardening, performance, and reliability — building a platform that scales with the industry"
        />
        <UseOfFundsCard
          icon={<Wifi className="w-5 h-5 text-[#FF2300]" />}
          title="Deep Integrations"
          description="Connecting across the music ecosystem — streaming platforms, booking systems, financial tools"
        />
        <UseOfFundsCard
          icon={<Star className="w-5 h-5 text-[#FF2300]" />}
          title="Marquee Artist Onboarding"
          description="Onboarding high-profile artists and management partners to drive credibility and adoption"
        />
        <UseOfFundsCard
          icon={<Users className="w-5 h-5 text-[#FF2300]" />}
          title="Founding Team & Execution"
          description="Key hires and founding team focus — ensuring the team can execute at the pace the market demands"
        />
        <UseOfFundsCard
          icon={<Handshake className="w-5 h-5 text-[#FF2300]" />}
          title="Infrastructure & Compliance"
          description="Secure, scalable infrastructure — data protection, compliance, and beta expansion"
        />
      </div>

      <div className="mt-12 rounded-xl border border-[#FF2300]/20 bg-[#FF2300]/[0.04] p-6 max-w-2xl">
        <p className="text-lg font-medium text-white">
          DIZEE is building the modern operating system the music industry has been missing.
        </p>
        <p className="mt-2 text-[13px] text-zinc-400">
          The team is built. The product is in motion. Now it&apos;s about capturing the market.
        </p>
      </div>
    </div>
  )
}

/* ── Slide 18: Closing ──────────────────────────────────────────────────── */
function SlideClosing() {
  return (
    <div className="flex flex-col justify-center py-12">
      <div className="flex items-center gap-2.5 mb-8">
        <div className="w-6 h-6 bg-[#FF2300] rounded-sm flex-shrink-0" />
        <BrandLogo className="h-4 w-auto text-white" />
      </div>
      <div className="max-w-2xl">
        <p className="text-xl sm:text-2xl font-medium tracking-tight text-zinc-300 leading-snug">
          The new operating system and source of truth for modern music careers.
        </p>
      </div>
      <div className="mt-12 space-y-2">
        <p className="text-[14px] text-[#FF2300] font-medium">partners@dizee.com</p>
        <p className="text-[13px] text-zinc-500">www.dizee.com</p>
      </div>
    </div>
  )
}

/* ── Slide 19: Graveyard Divider ──────────────────────────────────────── */
function SlideGraveyard() {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <p className="text-[11px] font-semibold text-zinc-700 uppercase tracking-[0.3em]">Appendix</p>
      <div className="mt-4 w-16 h-px bg-zinc-800" />
    </div>
  )
}

/* ── Slide 20: Why Now ──────────────────────────────────────────────────── */
function SlideWhyNow() {
  return (
    <div>
      <SlideTag>Why Now</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        A generational shift.{' '}
        <span className="text-[#FF2300]">The timing is now.</span>
      </h2>

      <div className="mt-12 grid sm:grid-cols-2 gap-6">
        <WhyNowCard title="A generational industry shift">
          The music industry is undergoing a transformation comparable to analog-to-digital. Independent artists are the
          fastest-growing segment, with non-major labels reaching 29.7% market share in 2024.
          <span className="block text-[10px] text-zinc-700 mt-2">Source: MIDiA Research, 2024</span>
        </WhyNowCard>
        <WhyNowCard title="The decline of gatekeepers">
          The rise of DIY creators is reshaping power and workflows. Artists now expect label-grade tools
          without label control. A unified career operating system wasn&apos;t possible before due to fragmentation.
        </WhyNowCard>
        <WhyNowCard title="Complexity is increasing">
          Touring, marketing, and release cycles are more complex and demanding than ever. More music is released in a single day than in the entire year of 1989.
          <span className="block text-[10px] text-zinc-700 mt-2">Source: NME / Will Page Study, 2024</span>
        </WhyNowCard>
        <WhyNowCard title="Technology readiness has converged">
          AI, cloud infrastructure, and API ecosystems have matured to the point where building a true career operating system
          is finally possible. Technology, culture, and industry readiness have converged.
        </WhyNowCard>
      </div>

      <div className="mt-10 pt-6 border-t border-white/[0.06]">
        <p className="text-lg font-medium text-white max-w-2xl">
          The industry is ready for a single, modern career operating system.
        </p>
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   SLIDE REGISTRY
   ═══════════════════════════════════════════════════════════════════════════ */

const SLIDES: Slide[] = [
  { title: 'Title', component: SlideTitle },
  { title: 'Mission', component: SlideMission },
  { title: "What's Happening Now", component: SlideWhatsHappening },
  { title: 'Industry Trends', component: SlideIndustryTrends },
  { title: 'Current State', component: SlideProblemCurrentTools },
  { title: 'Problem', component: SlideProblemHighLevel },
  { title: 'Real Conversations', component: SlideExampleMessages },
  { title: 'DIZEE Centralizes', component: SlideCentralizes },
  { title: 'Platform', component: SlideProduct },
  { title: 'Market Size', component: SlideMarketSize },
  { title: 'Business Model', component: SlideBusinessModel },
  { title: 'Go-To-Market', component: SlideGTM },
  { title: 'Competition', component: SlideCompetition },
  { title: 'Competitive Advantages', component: SlideAdvantages },
  { title: 'Team', component: SlideTeam },
  { title: 'The Ask', component: SlideAsk },
  { title: 'Closing', component: SlideClosing },
  { title: 'Appendix', component: SlideGraveyard },
  { title: 'Why Now', component: SlideWhyNow },
]

/* ═══════════════════════════════════════════════════════════════════════════
   REUSABLE COMPONENTS
   ═══════════════════════════════════════════════════════════════════════════ */

function SlideTag({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-block text-[11px] font-semibold text-[#FF2300] uppercase tracking-widest">
      {children}
    </span>
  )
}

function TrendCard({
  title,
  accent = 'red',
  children,
}: {
  title: string
  accent?: 'red' | 'emerald' | 'orange'
  children: React.ReactNode
}) {
  const styles = {
    red: { border: 'border-red-500/10', bg: 'bg-red-500/[0.03]', title: 'text-red-300' },
    orange: { border: 'border-[#FF2300]/15', bg: 'bg-[#FF2300]/[0.04]', title: 'text-[#FF2300]' },
    emerald: { border: 'border-emerald-500/10', bg: 'bg-emerald-500/[0.03]', title: 'text-emerald-300' },
  }
  const s = styles[accent]
  return (
    <div className={`rounded-xl border ${s.border} ${s.bg} p-6`}>
      <h3 className={`text-base font-semibold ${s.title} mb-2`}>{title}</h3>
      <p className="text-[15px] text-zinc-400 leading-relaxed">{children}</p>
    </div>
  )
}

function ProblemStatement({
  icon,
  children,
}: {
  icon: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="flex items-start gap-4">
      <div className="mt-1 shrink-0 text-red-400/60">{icon}</div>
      <p className="text-base sm:text-lg text-zinc-300 leading-relaxed">{children}</p>
    </div>
  )
}

function BrokenToolCard({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-6">
      <div className="flex items-center gap-3 mb-3">
        <div className="text-zinc-500">{icon}</div>
        <h3 className="text-base font-semibold text-white">{title}</h3>
      </div>
      <p className="text-[15px] text-zinc-400 leading-relaxed">{children}</p>
    </div>
  )
}

function SolutionCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode
  title: string
  description: string
}) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-6">
      <div className="mb-3">{icon}</div>
      <h3 className="text-base font-semibold text-white mb-2">{title}</h3>
      <p className="text-[15px] text-zinc-400 leading-relaxed">{description}</p>
    </div>
  )
}

function ProductFeature({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode
  title: string
  description: string
}) {
  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-4 py-3">
      <div className="flex items-center gap-3 mb-1">
        <div className="text-[#FF2300] shrink-0">{icon}</div>
        <p className="text-[13px] font-medium text-white">{title}</p>
      </div>
      <p className="text-[11px] text-zinc-500 leading-relaxed pl-7">{description}</p>
    </div>
  )
}

function MarketSegmentCard({
  label,
  description,
  stat,
  statLabel,
  note,
  color,
  source,
  sourceUrl,
}: {
  label: string
  description: string
  stat: string
  statLabel: string
  note: string
  color: 'red' | 'violet' | 'emerald'
  source?: string
  sourceUrl?: string
}) {
  const colors = {
    red: { border: 'border-[#FF2300]/20', bg: 'bg-[#FF2300]/[0.04]', text: 'text-[#FF2300]' },
    violet: { border: 'border-violet-500/20', bg: 'bg-violet-500/[0.04]', text: 'text-violet-400' },
    emerald: { border: 'border-emerald-500/20', bg: 'bg-emerald-500/[0.04]', text: 'text-emerald-400' },
  }
  const c = colors[color]

  return (
    <div className={`rounded-xl border ${c.border} ${c.bg} p-6`}>
      <h3 className={`text-[15px] font-semibold ${c.text} mb-1`}>{label}</h3>
      <p className="text-[12px] text-zinc-500 mb-5">{description}</p>
      <p className={`text-4xl font-bold tracking-tight ${c.text}`}>{stat}</p>
      <p className="text-[11px] text-zinc-600 mt-1 mb-5">{statLabel}</p>
      <p className="text-[12px] text-zinc-400 leading-relaxed">{note}</p>
      {source && (
        sourceUrl ? (
          <a href={sourceUrl} target="_blank" rel="noopener noreferrer" className="block text-[10px] text-zinc-700 mt-3 hover:text-zinc-500 transition-colors">Source: {source}</a>
        ) : (
          <p className="text-[10px] text-zinc-700 mt-3">Source: {source}</p>
        )
      )}
    </div>
  )
}

function PricingCard({
  tier,
  subtitle,
  price,
  audience,
  highlighted = false,
  note,
  features,
  plusFeatures,
}: {
  tier: string
  subtitle?: string
  price: string
  audience: string
  highlighted?: boolean
  note: string
  features: string[]
  plusFeatures?: string[]
}) {
  return (
    <div className={`rounded-xl border p-5 ${
      highlighted
        ? 'border-[#FF2300]/30 bg-[#FF2300]/[0.06] ring-1 ring-[#FF2300]/20'
        : 'border-white/[0.06] bg-white/[0.02]'
    }`}>
      <div className="flex items-center gap-2 mb-1">
        <h3 className="text-sm font-semibold text-white">{tier}</h3>
        {highlighted && (
          <span className="text-[9px] font-semibold text-[#FF2300] bg-[#FF2300]/10 rounded-full px-2 py-0.5">
            Popular
          </span>
        )}
      </div>
      {subtitle && <p className="text-[11px] text-zinc-600 mb-2">{subtitle}</p>}
      <p className="text-2xl font-bold tracking-tight text-white">
        {price}<span className="text-[12px] font-normal text-zinc-500 ml-1">{note}</span>
      </p>
      <p className="text-[11px] text-[#FF2300] mt-1 font-medium">{audience}</p>
      <ul className="mt-4 space-y-1.5">
        {features.map((feature, i) => (
          <li key={i} className="flex items-start gap-2">
            <Check className="w-3.5 h-3.5 mt-0.5 text-zinc-600 shrink-0" />
            <p className="text-[12px] text-zinc-400 leading-relaxed">{feature}</p>
          </li>
        ))}
      </ul>
      {plusFeatures && plusFeatures.length > 0 && (
        <>
          <div className="my-3 border-t border-white/[0.06]" />
          <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">Plus</p>
          <ul className="space-y-1.5">
            {plusFeatures.map((feature, i) => (
              <li key={i} className="flex items-start gap-2">
                <Check className="w-3.5 h-3.5 mt-0.5 text-[#FF2300] shrink-0" />
                <p className="text-[12px] text-zinc-300 leading-relaxed">{feature}</p>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

function RevenueStream({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2.5 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
      <DollarSign className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
      <p className="text-[12px] text-zinc-400">{children}</p>
    </div>
  )
}

function GTMPhaseCard({
  phase,
  title,
  timeline,
  target,
  items,
}: {
  phase: string
  title: string
  timeline: string
  target: string
  items: string[]
}) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-6">
      <span className="text-[11px] font-mono text-[#FF2300]/50 block">{phase}</span>
      <h3 className="text-[16px] font-semibold text-white mt-1">{title}</h3>
      <p className="text-[11px] text-zinc-600 mt-1">{timeline}</p>
      <ul className="mt-4 space-y-3">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2.5">
            <div className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 bg-[#FF2300]" />
            <p className="text-[13px] text-zinc-400 leading-relaxed">{item}</p>
          </li>
        ))}
      </ul>
      <div className="mt-5 pt-4 border-t border-white/[0.06]">
        <p className="text-[11px] text-zinc-600 uppercase tracking-widest">Target</p>
        <p className="text-[14px] font-semibold text-white mt-0.5">{target}</p>
      </div>
    </div>
  )
}

function ChannelBar({ label, pct }: { label: string; pct: number }) {
  return (
    <div className="flex-1">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] text-zinc-500">{label}</span>
        <span className="text-[11px] font-semibold text-white">{pct}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/[0.06]">
        <div className="h-full rounded-full bg-[#FF2300]" style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

/* ── Competition Quadrant (X: Static → 360° Career, Y: Disconnected → Intelligent) ── */
function CompetitionQuadrant() {
  const competitors: { name: string; x: number; y: number; highlight?: boolean }[] = [
    { name: 'Venice Music', x: 18, y: 22 },
    { name: 'Artist Growth', x: 28, y: 18 },
    { name: 'IndieFlow', x: 38, y: 20 },
    { name: 'Monday', x: 28, y: 72 },
    { name: 'Asana', x: 38, y: 68 },
    { name: 'Slack', x: 48, y: 74 },
    { name: 'Notion', x: 58, y: 70 },
    { name: 'DIZEE', x: 85, y: 88, highlight: true },
  ]

  return (
    <div className="relative w-full max-w-[700px] mx-auto">
      <svg viewBox="0 0 600 520" className="w-full h-auto" xmlns="http://www.w3.org/2000/svg">
        {/* Axes */}
        <line x1="300" y1="40" x2="300" y2="500" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" />
        <line x1="20" y1="270" x2="580" y2="270" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" />

        {/* X-axis: Static Software (left) → 360° Artist Career (right) */}
        <text x="20" y="265" fill="rgba(255,255,255,0.5)" fontSize="11" fontWeight="600">
          <tspan fill="#FF2300">X: </tspan>
          <tspan fill="white">Static Software</tspan>
        </text>
        <text x="580" y="265" textAnchor="end" fill="rgba(255,255,255,0.5)" fontSize="11" fontWeight="600">
          360° Artist Career
        </text>

        {/* Y-axis: Disconnected Workflows (top) → Intelligent Operating System (bottom) */}
        <text x="300" y="28" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="11" fontWeight="600">
          <tspan fill="#FF2300">Y: </tspan>
          <tspan fill="white">Disconnected Workflows</tspan>
        </text>
        <text x="300" y="512" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="11" fontWeight="600">
          Intelligent Operating System
        </text>

        {/* Competitor dots */}
        {competitors.map((c, i) => {
          const cx = 20 + (c.x / 100) * 560
          const cy = 40 + (c.y / 100) * 460
          return (
            <g key={i}>
              <circle
                cx={cx}
                cy={cy}
                r={c.highlight ? 14 : 8}
                fill={c.highlight ? '#FF2300' : 'rgba(255,255,255,0.8)'}
              />
              <text
                x={cx}
                y={cy + (c.highlight ? 28 : 22)}
                textAnchor="middle"
                fill={c.highlight ? '#FF2300' : 'rgba(255,255,255,0.6)'}
                fontSize={c.highlight ? '14' : '12'}
                fontWeight={c.highlight ? '700' : '600'}
              >
                {c.name}
              </text>
            </g>
          )
        })}
      </svg>
    </div>
  )
}

function AdvantageCard({
  icon,
  children,
}: {
  icon: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="flex items-start gap-4 rounded-xl border border-white/[0.06] bg-white/[0.02] p-5">
      <div className="shrink-0 mt-0.5">{icon}</div>
      <p className="text-[13px] text-zinc-300 leading-relaxed">{children}</p>
    </div>
  )
}

function TeamCard({
  name,
  role,
  points,
}: {
  name: string
  role: string
  points: string[]
}) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF2300]/30 to-[#FF4500]/20 border border-white/[0.08] flex items-center justify-center">
          <span className="text-[13px] font-semibold text-white">
            {name.split(' ').map((n) => n[0]).join('')}
          </span>
        </div>
        <div>
          <h3 className="text-[14px] font-semibold text-white">{name}</h3>
          <p className="text-[11px] text-[#FF2300] font-medium">{role}</p>
        </div>
      </div>
      <ul className="space-y-2">
        {points.map((point, i) => (
          <li key={i} className="flex items-start gap-2">
            <div className="mt-1.5 h-1 w-1 rounded-full shrink-0 bg-zinc-600" />
            <p className="text-[12px] text-zinc-400 leading-relaxed">{point}</p>
          </li>
        ))}
      </ul>
    </div>
  )
}

function WhyNowCard({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-6">
      <h3 className="text-[15px] font-semibold text-white mb-2">{title}</h3>
      <p className="text-[13px] text-zinc-400 leading-relaxed">{children}</p>
    </div>
  )
}

function UseOfFundsCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode
  title: string
  description: string
}) {
  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-5">
      <div className="mb-2">{icon}</div>
      <h3 className="text-[14px] font-semibold text-white mb-1">{title}</h3>
      <p className="text-[12px] text-zinc-400 leading-relaxed">{description}</p>
    </div>
  )
}
